import React, { useCallback, useContext, useEffect, useState } from 'react';
import * as styles from './Filters.module.scss';
import { FilterItem, FilterProps} from '../../typings';
import { fetchfilters } from '../../services/common.service';
import { motion, AnimatePresence } from 'framer-motion';
import { TabFilters } from '../../App';
import axios from 'axios';
const { __ } = window.wp.i18n;

export interface FilterOptions {
  data: FilterProps[];
}

export interface SelectedFilters {
  experience_level: Set<string>;
  professional_communities: Set<string>;
  location: Set<string>;
  contract_type: Set<string>;
  brand: Set<string>;
}

interface FilterComponentProps {
  initialFilters: SelectedFilters;
  settingFilters?: FilterTypes;
  onFiltersChange: (newFilters: SelectedFilters) => void;
  resetFilters: boolean;
  keyword?: string;
}

// get the filters selected in settings
interface Window {
  cg_jobs_es_var?: { pathName ?: string};
  cg_jobs_country_code?: string;
  cg_jobs_translated_filters?: { string: { string: string } };
  cg_jobs_tranlsation_filters_selections?: FilterTypes;
}
const countryCodeinSettings = (window as Window).cg_jobs_country_code;
const cCode = countryCodeinSettings ? countryCodeinSettings : (window as Window).cg_jobs_es_var?.pathName;
export type FilterTypes = 'experience_level' | 'professional_communities' | 'location' | 'contract_type' | 'brand';

export const decodeHtmlEntities = (html: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = html;
  return textArea.value;
};

export const Filters: React.FC<FilterComponentProps> = React.memo(({
  initialFilters,
  onFiltersChange,
  settingFilters,
  resetFilters,
  keyword
}) => {
  const [filterOptions, setFilterOptions] = useState<FilterProps[] | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(transformSelectedFilters(initialFilters));
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedFilterType, setSelectedFilterType] = useState<FilterTypes>();
  const visibleFilters = settingFilters? settingFilters : ['experience_level', 'professional_communities', 'location', 'contract_type', 'brand']
  const [experience_level, setExperience_level] = useState<FilterItem[]>([]);
  const [professional_communities, setProfessional_communities] = useState<FilterItem[]>([]);
  const [location, setLocation] = useState<FilterItem[]>([]);
  const [contract_type, setContract_type] = useState<FilterItem[]>([]);
  const [brand, setBrand] = useState<FilterItem[]>([]);

  // Fix the order of Filter Taxonomy
  const orderedTypes = ['experience_level', 'professional_communities', 'location', 'contract_type', 'brand'];
  const tabFilters = useContext(TabFilters);

  useEffect(() => {
    // Fetch filter options from API
    const fetchFilterOptions = async () => {
      try {
        const cleanedFilters = Object.fromEntries(
          Object.entries(selectedFilters).filter(([_, value]) => value instanceof Set ? value.size > 0 : Boolean(value))
        );

        const response = await fetchfilters({filters: Object.fromEntries(Object.entries(cleanedFilters).map(([key, value]) => [key, Array.from(value)])), countryCode: cCode, filter: selectedFilterType, search: keyword});
        const data: FilterOptions = response;
        
        const orderedData = orderedTypes.map(type => {
          return data.data.find(item => item.type === type) as FilterProps;
        }).filter(Boolean) as FilterProps[];
       
        setFilterOptions(orderedData);
        setLoading(false);

      } catch (error) {
        console.error('Error fetching filter options:', error);
        setLoading(false);
      }
    };

    fetchFilterOptions();
  }, [selectedFilters, keyword]);

  useEffect(() => {
    setSelectedFilters(initialFilters);
  }, [initialFilters]);

  useEffect(() => {
    if(resetFilters && Object.keys(selectedFilters).length !== 0 ) document.getElementById('clearAllFilters')?.click();
  }, [resetFilters])

  function transformSelectedFilters(selectedFilters: SelectedFilters): SelectedFilters {
    // Iterate through the selectedFilters object
    for (const [key, valueSet] of Object.entries(selectedFilters)) {
      // Create a new Set to hold the transformed values
      const transformedSet = new Set<string>();
  
      // Iterate through each value in the Set
      valueSet.forEach((value:string) => {
        // Split each string by commas and add individual values to the new Set
        value.split(',').forEach(val => transformedSet.add(val.trim()));
      });
  
      // Replace the original Set with the transformed one
      selectedFilters[key as keyof SelectedFilters] = transformedSet;
    }
    return selectedFilters;
  }
  
  const handleCheckboxChange = useCallback((type: FilterTypes, value: string) => { 
    setSelectedFilters(prevFilters => {
      const newSelection = new Set(prevFilters[type]);
      if (newSelection.has(value)) {
        newSelection.delete(value);
      } else {
        newSelection.add(value);
      }
      const updatedFilters = { ...prevFilters, [type]: newSelection };
      onFiltersChange(updatedFilters);

      // Check if the new selection is empty
      if (newSelection.size === 0) {
        setSelectedFilterType(undefined);
      }

      // Check if the new selection is empty
      if (newSelection.size === 0) {
        setSelectedFilterType(undefined);
      }

      return updatedFilters;
    });
    
  }, []);

  const clearFilter = useCallback((type: FilterTypes) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = { ...prevFilters, [type]: new Set<string>() };
      onFiltersChange(updatedFilters);
      return updatedFilters;
    });
  }, []);

  const clearAllFilters = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setSelectedFilters({
      experience_level: new Set<string>(),
      location: new Set<string>(),
      brand: new Set<string>(),
      professional_communities: new Set<string>(),
      contract_type: new Set<string>(),
    });

    onFiltersChange({
      experience_level: new Set<string>(),
      location: new Set<string>(),
      brand: new Set<string>(),
      professional_communities: new Set<string>(),
      contract_type: new Set<string>(),
    });
    setSelectedFilterType(undefined);
    e.preventDefault();
  };

  function updateLabel(label:string, filterType: string){
    if ((window as Window).cg_jobs_tranlsation_filters_selections?.includes(filterType)) {
      if ((window as Window).cg_jobs_translated_filters && filterType in (window as Window).cg_jobs_translated_filters && encodeURIComponent(label) in (window as Window).cg_jobs_translated_filters[filterType] && (window as Window).cg_jobs_translated_filters[filterType][encodeURIComponent(label)] !== '') {
        return (window as Window).cg_jobs_translated_filters[filterType][encodeURIComponent(label)];
      }
      
    }
    const updatedLabel = label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' ');
    return updatedLabel.replace(/,(\S)/g, ', $1');
  }

  const getFilterLabel = (type: string): string => {
    switch (type) {
      case 'professional_communities':
        /* translators: This is the label for the professions filter */
        return __('Professions', 'cg-jobs');
      case 'brand':
        return __('Brands', 'cg-jobs');
      case 'location':
        return __('Location', 'cg-jobs');
      case 'experience_level':
        return __('Experience Level', 'cg-jobs');
      case 'contract_type':
        return __('Contract Type', 'cg-jobs');
      default:
        return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    }
  };

  const getFilterClearAriaLabel = (type: string): string => {
    switch (type) {
      case 'professional_communities':
        return __('Click to clear Professions', 'cg-jobs');
      case 'brand':
        return __('Click to clear Brands', 'cg-jobs');
      case 'location':
        return __('Click to clear Location', 'cg-jobs');
      case 'experience_level':
        /* translators: Aria-label for clearing Experience Level filter */
        return __('Click to clear Experience Level', 'cg-jobs');
      case 'contract_type':
        return __('Click to clear Contract Type', 'cg-jobs');
      default:
        return __('Click to clear', 'cg-jobs') + ' ' + type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    }
  };
  
  return (
    <div className={styles['filter-wrapper']} id="filter-wrapper">    
      <div className={styles['filter-container']} id="filter-container">
      { loading ? (
          <div>{ __( 'Loading...', 'cg-jobs' ) }</div>
        ) : (
        ((filterOptions && filterOptions.every(obj => Array.isArray(obj.item) && obj.item.length !== 0))) ? (
          <>
            <div className={styles['filter-header']}>
              <h2 className={styles['filter-title']}>{ __( 'Filters', 'cg-jobs' ) }</h2>
              {Object.entries(selectedFilters).some(([key, set]) => (key !== "country_code" && set.size > 0)) && <button className={styles['filter-button']} id="clearAllFilters" onClick={(e) => clearAllFilters(e)}>{ __( 'Clear all filters', 'cg-jobs' ) }</button>}
            </div>

            {filterOptions && filterOptions.map(singleFilter => ( 
              (singleFilter.type && singleFilter.type !== "" && visibleFilters.includes(singleFilter.type) && singleFilter.item.length > 0) && 
                <div className={styles['filter-group']} key={singleFilter.type}>
                    <div className={[styles['filter-header'], styles['mb2']].join(' ')}>
                    <h3>{getFilterLabel(singleFilter.type)}</h3>
                    <button
                      className={styles['filter-button']}
                      aria-label={getFilterClearAriaLabel(singleFilter.type)}
                      onClick={() => clearFilter(singleFilter.type as FilterTypes)}
                    >
                      {__('Clear', 'cg-jobs')}
                    </button>
                    </div>
                    <AnimatePresence>
                      <motion.ul 
                        className={[styles['filter-list'], tabFilters ? styles['tabFilterOpen'] : ''].join(" ")} 
                        id={`filter-list-${singleFilter.type}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {singleFilter.item && singleFilter.item.sort((a,b) => b.count - a.count).map(childItem => (
                          <motion.li 
                            key={childItem.value} 
                            data-count={childItem.count} 
                            className={childItem.count === 0 ? styles['no-job'] : ''}
                            layout 
                            transition={{ type: "spring", stiffness: 1200, damping: 60 }}
                          >
                          <input 
                            type="checkbox"
                            id={`${childItem.value}-${singleFilter.type}`}
                            checked={selectedFilters[singleFilter.type as FilterTypes].has(childItem.value)}
                            onChange={() => {            
                              setSelectedFilterType(singleFilter.type as FilterTypes);
                              handleCheckboxChange(singleFilter.type as FilterTypes, childItem.value);                       
                            }}
                            onKeyDown={(e) => { 
                              if (e.keyCode == 13){         
                                setSelectedFilterType(singleFilter.type as FilterTypes);
                                handleCheckboxChange(singleFilter.type as FilterTypes, childItem.value);
                              }                         
                            }}
                            tabIndex={childItem.count === 0 ? -1 : 0}
                          />
                          <label htmlFor={`${childItem.value}-${singleFilter.type}`}><span className={styles['label-text']}>{updateLabel(childItem.value, singleFilter.type)} ({childItem.count})</span></label>
                          </motion.li>
                        ))}
                      </motion.ul>
                    </AnimatePresence>
                  </div>
            ))}
          </>
        ) : (
          <div>{ __( 'No filter options available.', 'cg-jobs' ) }</div>
        )
      )}
           
      </div>
    </div>
)});